import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Info, CircleCheck, TriangleAlert, CircleX, Palmtree, Settings, Briefcase, FileText, CreditCard, CalendarClock, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { notificationsAPI } from '../services/api';
import { toast } from 'sonner';
import { triggerGlobalRefresh } from '../context/RefreshContext';

const typeIcon = (type, entity) => {
  const c = 'w-4 h-4 shrink-0 mt-0.5';
  // Entity icon takes priority if relevant (booking/invoice/payment/leave/calendar)
  if (entity === 'calendar_event') return <CalendarClock className={`${c} text-violet-500`} />;
  if (entity === 'booking') return <Briefcase className={`${c} text-blue-500`} />;
  if (entity === 'invoice') return <FileText className={`${c} text-purple-500`} />;
  if (entity === 'payment') return <CreditCard className={`${c} text-amber-500`} />;
  if (entity === 'leave')   return <Palmtree className={`${c} text-cyan-500`} />;
  if (type === 'event_reminder') return <CalendarClock className={`${c} text-violet-500`} />;
  switch (type) {
    case 'success': return <CircleCheck className={`${c} text-emerald-500`} />;
    case 'warning': return <TriangleAlert className={`${c} text-amber-500`} />;
    case 'error':   return <CircleX       className={`${c} text-red-500`} />;
    default:        return <Info          className={`${c} text-navy-400`} />;
  }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'przed chwilą';
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} dni temu`;
  return new Date(iso).toLocaleDateString('pl-PL');
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const r = await notificationsAPI.unreadCount();
      setCount(r.data.count || 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await notificationsAPI.list({ limit: 30 });
      setItems(r.data || []);
    } catch {
      toast.error('Nie udało się pobrać powiadomień');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    pollRef.current = setInterval(fetchCount, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const handleOpenNotification = async (n) => {
    // Mark as read + navigate to deep-link if available
    if (!n.read) await handleMarkRead(n.id);
    if (n.route) {
      setOpen(false);
      const url = n.highlight_id ? `${n.route}?highlight=${n.highlight_id}` : n.route;
      navigate(url);
      // Trigger refresh of the destination page's data so the new entity is visible
      // immediately (no F5 needed). Map route → scope.
      const scopeMap = {
        '/leaves': 'leaves',
        '/bookings': 'bookings',
        '/payments': 'payments',
        '/clients': 'clients',
        '/invoices': 'invoices',
        '/contracts': 'contracts',
        '/calendar': 'calendar',
      };
      const scope = scopeMap[n.route] || null;
      // Fire after a tiny delay so the page has time to mount and subscribe
      setTimeout(() => triggerGlobalRefresh(scope), 150);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setCount(0);
      toast.success('Oznaczone jako przeczytane');
    } catch { toast.error('Błąd'); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(id);
      setItems(prev => prev.filter(n => n.id !== id));
      const wasUnread = items.find(n => n.id === id && !n.read);
      if (wasUnread) setCount(c => Math.max(0, c - 1));
    } catch { toast.error('Błąd usuwania'); }
  };

  const handleSnooze = async (id, e, minutes = 15) => {
    e.stopPropagation();
    try {
      await notificationsAPI.snooze(id, minutes);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true, snoozed: true } : n));
      setCount(c => Math.max(0, c - 1));
      toast.success(`Odłożone o ${minutes} min`);
    } catch { toast.error('Nie udało się odłożyć'); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative hover:bg-navy-50 ${count > 0 ? 'w-auto px-2 gap-1.5' : ''}`}
          data-testid="notifications-bell"
          aria-label={`Powiadomienia${count > 0 ? ` (${count} nowe)` : ''}`}
        >
          <Bell className={`w-5 h-5 ${count > 0 ? 'text-red-500' : 'text-navy-700'}`} />
          {count > 0 && (
            <>
              {/* Inline count next to bell — visible "🔔 12" pattern requested by user */}
              <span
                className="text-sm font-bold text-red-500 leading-none"
                data-testid="notifications-count-inline"
              >
                {count > 99 ? '99+' : count}
              </span>
              {/* Small pulsing dot for added attention */}
              <span
                className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 animate-pulse"
                data-testid="notifications-badge"
              />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-navy-800 text-white rounded-t-md">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gold-400" />
            <span className="font-semibold text-sm">Powiadomienia</span>
            {count > 0 && <span className="text-[10px] bg-gold-500 text-navy-800 px-1.5 py-0.5 rounded-full font-bold">{count} nowe</span>}
          </div>
          {count > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-[11px] text-gold-300 hover:text-gold-400 flex items-center gap-1"
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="w-3 h-3" />Oznacz wszystkie
            </button>
          )}
        </div>

        <ScrollArea className="max-h-[480px]">
          {loading ? (
            <div className="py-8 text-center text-xs text-muted-foreground">Ładowanie...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center" data-testid="notifications-empty">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Brak powiadomień</p>
            </div>
          ) : (
            <div className="divide-y">
              {items.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleOpenNotification(n)}
                  className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    n.read ? 'bg-white hover:bg-slate-50' : 'bg-gold-50/40 hover:bg-gold-50'
                  }`}
                  data-testid={`notification-${n.id}`}
                  title={n.route ? `Przejdź do ${n.route}` : ''}
                >
                  {typeIcon(n.type, n.related_entity_type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm truncate ${n.read ? 'font-medium text-navy-700' : 'font-semibold text-navy-800'}`}>
                        {n.title}
                      </p>
                      {!n.read && <span className="w-2 h-2 bg-gold-500 rounded-full shrink-0 mt-1.5" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-wrap">{n.message}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {(n.type === 'event_reminder' || n.related_entity_type === 'calendar_event') && !n.read && (
                          <button
                            onClick={(e) => handleSnooze(n.id, e, 15)}
                            className="text-[10px] text-violet-600 hover:text-violet-700 flex items-center gap-0.5"
                            title="Odłóż 15 min"
                            data-testid={`notification-snooze-${n.id}`}
                          >
                            <Clock className="w-3 h-3" />
                          </button>
                        )}
                        {!n.read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                            className="text-[10px] text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5"
                            title="Oznacz jako przeczytane"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(n.id, e)}
                          className="text-[10px] text-red-500 hover:text-red-600"
                          title="Usuń"
                          data-testid={`notification-delete-${n.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t px-4 py-2 bg-slate-50 rounded-b-md">
          <button
            onClick={() => { setOpen(false); navigate('/ustawienia-powiadomien'); }}
            className="text-xs text-navy-600 hover:text-navy-800 flex items-center gap-1.5 w-full"
            data-testid="notifications-preferences-link"
          >
            <Settings className="w-3 h-3" />Ustawienia powiadomień
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
